import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import { Dialog, IconButton, Typography, DialogContent, DialogTitle } from '@mui/material';
import { DateTime } from 'luxon';
import { YellowButton } from '@/components/button/button';
import { ExperienceEventFieldsFragment } from '@/generated/graphql';
import CloseButtonSvg from '../svgs/close-button.svg';
import { useJoinEvent } from '../hooks/use-join-event/use-join-event';

const MainDiv = styled(Dialog)`
  z-index: 9999;
  a {
    color: #5c91c6;
  }
`;
const CloseButton = styled(IconButton)`
  position: absolute;
  z-index: 99;
  right: 5px;
  top: 5px;
  color: black;
`;

const Title = styled(Typography)`
  font-family: var(--font-work-sans);
  font-style: normal;
  font-weight: 500;
  font-size: 28px;
  line-height: 127.3%;
  color: #231727;
`;
const ContentDiv = styled(DialogContent)`
  width: 523px;
  max-height: 554px;
  padding: 0 30px 30px 30px;
  display: block;
  margin-bottom: 32px;
`;
const Text = styled(Typography)`
  font-weight: 600;
  font-family: var(--font-work-sans);
  font-size: 16px;
  line-height: 136%;
  color: #231727;
  text-align: start;
`;
const Row = styled.div`
  width: 100%;
  padding: 18px 0;
  height: auto;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
    padding: 15px 0;
    row-gap: 15px;
  }
`;
const DateTimeDiv = styled.div`
  width: 50%;
  height: auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  @media (max-width: 768px) {
    width: 100%;
  }
`;
const TimeSlot = styled(Text)`
  font-weight: normal;
  color: #828282;
`;
const Line = styled.div`
  border-bottom: 1px solid #e0e0e0;
`;

export type EventTimeProps = {
  event: ExperienceEventFieldsFragment;
};

const EventTime = ({ event }: EventTimeProps) => {
  const startDate = DateTime.fromISO(event.start);
  const endDate = DateTime.fromISO(event.end);
  return (
    <DateTimeDiv>
      <Text>{startDate.toLocaleString(DateTime.DATE_FULL)}</Text>
      <TimeSlot>
        {startDate.toLocaleString(DateTime.TIME_SIMPLE)} -{' '}
        {endDate.toLocaleString(DateTime.TIME_SIMPLE)} {startDate.toFormat('ZZZZ')}
      </TimeSlot>
    </DateTimeDiv>
  );
};

export type EventBookingModalProps = {
  events: ExperienceEventFieldsFragment[];
  open: boolean;
  onClose: () => void;
};

const EventBookingModal = ({ events, open, onClose }: EventBookingModalProps) => {
  const { joinEvent } = useJoinEvent();
  return (
    <MainDiv open={open} maxWidth="sm" onClose={onClose}>
      <Global
        styles={css`
          .MuiDialog-paper {
            border-radius: 8px;
          }
          .MuiDialogTitle-root {
            padding-top: 90px;
            padding-left: 30px;
          }
        `}
      />
      <CloseButton onClick={onClose}>
        <CloseButtonSvg />
      </CloseButton>
      <DialogTitle id="group-booking">
        <Title>Book a time</Title>
      </DialogTitle>
      <ContentDiv>
        {events.map(event => (
          <div key={event.id}>
            <Row>
              <EventTime event={event} />
              <YellowButton onClick={() => joinEvent(event.id)}>Book Experience</YellowButton>
            </Row>
            <Line />
          </div>
        ))}
      </ContentDiv>
    </MainDiv>
  );
};

export default EventBookingModal;
